import type {
  DetectedRecoveryInterval,
  DetectedWorkInterval,
  IntervalClassification,
  IntervalDetectionConfiguration,
  IntervalDetectionResult,
  RideRecord
} from "../types/ride";

type IntervalDetectionInputConfiguration = Partial<IntervalDetectionConfiguration>;

type TimedSample = {
  absoluteSeconds: number;
  relativeSeconds: number;
  timestamp: string | null;
  powerWatts: number | null;
  heartRateBpm: number | null;
  cadenceRpm: number | null;
};

type TimelineSecond = {
  secondIndex: number;
  elapsedSeconds: number;
  timestamp: string | null;
  powerWatts: number | null;
  heartRateBpm: number | null;
  cadenceRpm: number | null;
  stopped: boolean;
};

type IntervalBlock = {
  startSecond: number;
  endSecond: number;
};

type ClassificationProfile = {
  classification: Exclude<IntervalClassification, "unclassified">;
  minDurationSeconds: number;
  maxDurationSeconds: number;
  minRatio: number;
  maxRatio: number;
};

type BlockDetectionOptions = {
  startHysteresisSeconds: number;
  endHysteresisSeconds: number;
  maxPowerDipSeconds: number;
  maxCadenceInterruptionSeconds: number;
  rampLeadInSeconds: number;
  startThresholdRatioOffset: number;
  endThresholdRatioOffset: number;
};

const DEFAULT_CONFIGURATION: IntervalDetectionConfiguration = {
  longGapMultiplier: 5,
  maxMergeGapSeconds: 25,
  maxShortRecoveryMergeSeconds: 12,
  shortRecoveryMergeMinPowerRatio: 0.5,
  startHysteresisSeconds: 4,
  endHysteresisSeconds: 6,
  maxPowerDipSeconds: 8,
  maxCadenceInterruptionSeconds: 4,
  rampLeadInSeconds: 12,
  startThresholdRatioOffset: 0.08,
  endThresholdRatioOffset: 0.12,
  minSprintDurationSeconds: 6,
  minAnaerobicDurationSeconds: 20,
  minVo2maxDurationSeconds: 120,
  minThresholdDurationSeconds: 300,
  minSweetSpotDurationSeconds: 480,
  minTempoDurationSeconds: 600,
  minEnduranceDurationSeconds: 900,
  minRecoveryDurationSeconds: 20,
  minConfidence: 0.35,
  maxSingleEnduranceCoverage: 0.6
};

const CLASSIFICATION_PROFILES: ClassificationProfile[] = [
  {
    classification: "sprint",
    minDurationSeconds: 1,
    maxDurationSeconds: 20,
    minRatio: 1.5,
    maxRatio: Number.POSITIVE_INFINITY
  },
  {
    classification: "anaerobic",
    minDurationSeconds: 20,
    maxDurationSeconds: 180,
    minRatio: 1.2,
    maxRatio: Number.POSITIVE_INFINITY
  },
  {
    classification: "vo2max",
    minDurationSeconds: 120,
    maxDurationSeconds: 480,
    minRatio: 1.06,
    maxRatio: 1.3
  },
  {
    classification: "threshold",
    minDurationSeconds: 300,
    maxDurationSeconds: 2400,
    minRatio: 0.91,
    maxRatio: 1.05
  },
  {
    classification: "sweet-spot",
    minDurationSeconds: 480,
    maxDurationSeconds: 3600,
    minRatio: 0.84,
    maxRatio: 0.94
  },
  {
    classification: "tempo",
    minDurationSeconds: 600,
    maxDurationSeconds: 5400,
    minRatio: 0.76,
    maxRatio: 0.9
  },
  {
    classification: "endurance",
    minDurationSeconds: 900,
    maxDurationSeconds: 10800,
    minRatio: 0.56,
    maxRatio: 0.75
  }
];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  return null;
}

function toRecordTimeSeconds(record: RideRecord): number | null {
  const elapsedSeconds = toFiniteNumber(record.elapsedSeconds);
  if (elapsedSeconds !== null) {
    return elapsedSeconds;
  }

  if (record.timestamp) {
    const timestampMilliseconds = Date.parse(record.timestamp);
    if (!Number.isNaN(timestampMilliseconds)) {
      return timestampMilliseconds / 1000;
    }
  }

  return null;
}

function normalizeFtp(ftpInput: number | null | undefined): number | null {
  if (typeof ftpInput !== "number" || !Number.isFinite(ftpInput) || ftpInput <= 0) {
    return null;
  }

  return ftpInput;
}

function getMedian(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }

  const sortedValues = [...values].sort((left, right) => left - right);
  const middleIndex = Math.floor(sortedValues.length / 2);
  if (sortedValues.length % 2 === 1) {
    return sortedValues[middleIndex];
  }

  return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2;
}

function computeNormalizedPower(powerBySecond: Array<number | null>): number | null {
  const normalized = powerBySecond.map(value => value ?? 0);
  if (normalized.length < 30) {
    return null;
  }

  const prefix = new Array<number>(normalized.length + 1).fill(0);
  for (let index = 0; index < normalized.length; index += 1) {
    prefix[index + 1] = prefix[index] + normalized[index];
  }

  const fourthPowers: number[] = [];
  for (let start = 0; start <= normalized.length - 30; start += 1) {
    const end = start + 30;
    const averagePower = (prefix[end] - prefix[start]) / 30;
    fourthPowers.push(averagePower ** 4);
  }

  if (fourthPowers.length === 0) {
    return null;
  }

  const meanFourthPower = fourthPowers.reduce((sum, value) => sum + value, 0) / fourthPowers.length;
  return meanFourthPower > 0 ? meanFourthPower ** 0.25 : 0;
}

function average(values: Array<number | null>): number | null {
  const valid = values.filter((value): value is number => value !== null && Number.isFinite(value));
  if (valid.length === 0) {
    return null;
  }

  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

function max(values: Array<number | null>): number | null {
  const valid = values.filter((value): value is number => value !== null && Number.isFinite(value));
  if (valid.length === 0) {
    return null;
  }

  return Math.max(...valid);
}

function mergeConfiguration(configuration?: IntervalDetectionInputConfiguration): IntervalDetectionConfiguration {
  return {
    ...DEFAULT_CONFIGURATION,
    ...(configuration ?? {})
  };
}

function buildTimeline(records: RideRecord[], configuration: IntervalDetectionConfiguration): { timeline: TimelineSecond[]; medianIntervalSeconds: number | null } {
  const samples: TimedSample[] = records
    .map(record => {
      const absoluteSeconds = toRecordTimeSeconds(record);
      return {
        absoluteSeconds,
        relativeSeconds: 0,
        timestamp: record.timestamp ?? null,
        powerWatts: toFiniteNumber(record.powerWatts),
        heartRateBpm: toFiniteNumber(record.heartRateBpm),
        cadenceRpm: toFiniteNumber(record.cadenceRpm)
      };
    })
    .filter(sample => sample.absoluteSeconds !== null)
    .map(sample => ({
      ...sample,
      absoluteSeconds: sample.absoluteSeconds as number
    }))
    .sort((left, right) => left.absoluteSeconds - right.absoluteSeconds);

  if (samples.length < 2) {
    return { timeline: [], medianIntervalSeconds: null };
  }

  const deduplicatedSamples: TimedSample[] = [];
  for (const sample of samples) {
    const previous = deduplicatedSamples[deduplicatedSamples.length - 1];
    if (!previous || previous.absoluteSeconds !== sample.absoluteSeconds) {
      deduplicatedSamples.push(sample);
      continue;
    }

    deduplicatedSamples[deduplicatedSamples.length - 1] = sample;
  }

  if (deduplicatedSamples.length < 2) {
    return { timeline: [], medianIntervalSeconds: null };
  }

  const baseAbsoluteSeconds = deduplicatedSamples[0].absoluteSeconds;
  const normalizedSamples = deduplicatedSamples.map(sample => ({
    ...sample,
    relativeSeconds: sample.absoluteSeconds - baseAbsoluteSeconds
  }));

  const intervals = normalizedSamples
    .slice(1)
    .map((sample, index) => sample.relativeSeconds - normalizedSamples[index].relativeSeconds)
    .filter(interval => interval > 0);

  const medianIntervalSeconds = getMedian(intervals);
  if (medianIntervalSeconds === null || medianIntervalSeconds <= 0) {
    return { timeline: [], medianIntervalSeconds: null };
  }

  const lastRelativeSeconds = normalizedSamples[normalizedSamples.length - 1].relativeSeconds;
  const timelineLength = Math.max(1, Math.ceil(lastRelativeSeconds));
  const powerWeighted = new Array<number>(timelineLength).fill(0);
  const powerCoverage = new Array<number>(timelineLength).fill(0);
  const heartRateWeighted = new Array<number>(timelineLength).fill(0);
  const heartRateCoverage = new Array<number>(timelineLength).fill(0);
  const cadenceWeighted = new Array<number>(timelineLength).fill(0);
  const cadenceCoverage = new Array<number>(timelineLength).fill(0);
  const stoppedCoverage = new Array<number>(timelineLength).fill(0);

  const longGapThreshold = medianIntervalSeconds * configuration.longGapMultiplier;

  for (let index = 0; index < normalizedSamples.length - 1; index += 1) {
    const current = normalizedSamples[index];
    const next = normalizedSamples[index + 1];
    const start = current.relativeSeconds;
    const end = next.relativeSeconds;
    const duration = end - start;

    if (duration <= 0) {
      continue;
    }

    const isLongGap = duration > longGapThreshold;
    const startSecond = Math.floor(start);
    const endSecond = Math.ceil(end) - 1;

    for (let second = startSecond; second <= endSecond; second += 1) {
      if (second < 0 || second >= timelineLength) {
        continue;
      }

      const overlapStart = Math.max(start, second);
      const overlapEnd = Math.min(end, second + 1);
      const overlapDuration = overlapEnd - overlapStart;

      if (overlapDuration <= 0) {
        continue;
      }

      if (isLongGap) {
        stoppedCoverage[second] += overlapDuration;
        continue;
      }

      if (current.powerWatts !== null) {
        powerWeighted[second] += current.powerWatts * overlapDuration;
        powerCoverage[second] += overlapDuration;
      }

      if (current.heartRateBpm !== null) {
        heartRateWeighted[second] += current.heartRateBpm * overlapDuration;
        heartRateCoverage[second] += overlapDuration;
      }

      if (current.cadenceRpm !== null) {
        cadenceWeighted[second] += current.cadenceRpm * overlapDuration;
        cadenceCoverage[second] += overlapDuration;
      }
    }
  }

  const hasTimestamp = normalizedSamples.some(sample => sample.timestamp !== null);
  const baseTimestampMilliseconds = hasTimestamp && normalizedSamples[0].timestamp
    ? Date.parse(normalizedSamples[0].timestamp)
    : null;

  const timeline: TimelineSecond[] = [];
  for (let second = 0; second < timelineLength; second += 1) {
    const stopped = stoppedCoverage[second] >= 0.5;
    const powerWatts = powerCoverage[second] > 0 ? powerWeighted[second] / powerCoverage[second] : null;
    const heartRateBpm = heartRateCoverage[second] > 0 ? heartRateWeighted[second] / heartRateCoverage[second] : null;
    const cadenceRpm = cadenceCoverage[second] > 0 ? cadenceWeighted[second] / cadenceCoverage[second] : null;

    timeline.push({
      secondIndex: second,
      elapsedSeconds: second,
      timestamp:
        baseTimestampMilliseconds !== null && Number.isFinite(baseTimestampMilliseconds)
          ? new Date(baseTimestampMilliseconds + second * 1000).toISOString()
          : null,
      powerWatts,
      heartRateBpm,
      cadenceRpm,
      stopped
    });
  }

  return {
    timeline,
    medianIntervalSeconds
  };
}

function movingAveragePowerBySecond(timeline: TimelineSecond[], windowSeconds: number): Array<number | null> {
  const values = timeline.map(item => (item.stopped ? null : item.powerWatts ?? 0));
  const validFlags = timeline.map(item => (!item.stopped ? 1 : 0));
  const prefixPower = new Array<number>(values.length + 1).fill(0);
  const prefixValid = new Array<number>(values.length + 1).fill(0);

  for (let index = 0; index < values.length; index += 1) {
    prefixPower[index + 1] = prefixPower[index] + (values[index] ?? 0);
    prefixValid[index + 1] = prefixValid[index] + validFlags[index];
  }

  const halfWindow = Math.floor(windowSeconds / 2);

  return values.map((_, index) => {
    const start = Math.max(0, index - halfWindow);
    const end = Math.min(values.length - 1, index + halfWindow);
    const validCount = prefixValid[end + 1] - prefixValid[start];

    if (validCount <= 0) {
      return null;
    }

    const totalPower = prefixPower[end + 1] - prefixPower[start];
    return totalPower / validCount;
  });
}

function detectBlocks(
  timeline: TimelineSecond[],
  smoothedPower: Array<number | null>,
  ftp: number,
  minRatio: number,
  minDurationSeconds: number,
  maxDipSeconds: number,
  options: BlockDetectionOptions
): IntervalBlock[] {
  const ratioSeries = smoothedPower.map(value => (value === null ? null : value / ftp));
  const blocks: IntervalBlock[] = [];
  const startThreshold = Math.max(0, Math.max(minRatio - options.startThresholdRatioOffset, minRatio * 0.9));
  const endThreshold = Math.max(0, Math.max(minRatio - options.endThresholdRatioOffset, minRatio * 0.92));
  const maxGapSeconds = Math.max(maxDipSeconds, options.maxPowerDipSeconds, options.endHysteresisSeconds);

  let start: number | null = null;
  let gapSeconds = 0;
  let targetSeconds = 0;

  const pushBlockIfValid = (endSecond: number) => {
    if (start === null) {
      return;
    }

    const durationSeconds = endSecond - start + 1;
    if (endSecond >= start && durationSeconds >= minDurationSeconds && targetSeconds >= options.startHysteresisSeconds) {
      blocks.push({ startSecond: start, endSecond });
    }
  };

  for (let index = 0; index < ratioSeries.length; index += 1) {
    const ratio = ratioSeries[index];
    const stopped = timeline[index].stopped;
    const cadence = timeline[index].cadenceRpm;
    const cadenceInterrupted = cadence !== null && cadence <= 2;
    const canStart = !stopped && ratio !== null && ratio >= minRatio;
    const reachesTarget = !stopped && ratio !== null && ratio >= minRatio;
    const canStayActive = !stopped && ratio !== null && ratio >= endThreshold;

    if (start === null) {
      if (!canStart) {
        continue;
      }

      let expandedStart = index;
      if (options.rampLeadInSeconds > 0) {
        const earliest = Math.max(0, index - options.rampLeadInSeconds);
        for (let scan = index - 1; scan >= earliest; scan -= 1) {
          const scanRatio = ratioSeries[scan];
          if (timeline[scan].stopped || scanRatio === null || scanRatio < startThreshold) {
            break;
          }
          expandedStart = scan;
        }
      }

      start = expandedStart;
      gapSeconds = 0;
      targetSeconds = 0;
    }

    if (canStayActive) {
      if (start === null) {
        start = index;
      }
      if (reachesTarget) {
        targetSeconds += 1;
      }
      gapSeconds = 0;
      continue;
    }

    if (start === null) {
      continue;
    }

    if (stopped) {
      const end = index - 1 - gapSeconds;
      pushBlockIfValid(end);
      start = null;
      gapSeconds = 0;
      targetSeconds = 0;
      continue;
    }

    if (cadenceInterrupted && gapSeconds < options.maxCadenceInterruptionSeconds) {
      gapSeconds += 1;
      continue;
    }

    gapSeconds += 1;
    if (gapSeconds > maxGapSeconds) {
      const end = index - gapSeconds;
      pushBlockIfValid(end);
      start = null;
      gapSeconds = 0;
      targetSeconds = 0;
    }
  }

  if (start !== null) {
    const end = ratioSeries.length - 1 - gapSeconds;
    pushBlockIfValid(end);
  }

  return blocks;
}

function mergeBlocks(
  blocks: IntervalBlock[],
  configuration: IntervalDetectionConfiguration,
  smoothThirtySecondPower: Array<number | null>,
  ftp: number
): IntervalBlock[] {
  if (blocks.length <= 1) {
    return blocks;
  }

  const sortedBlocks = [...blocks].sort((left, right) => left.startSecond - right.startSecond);
  const merged: IntervalBlock[] = [sortedBlocks[0]];

  for (let index = 1; index < sortedBlocks.length; index += 1) {
    const current = sortedBlocks[index];
    const previous = merged[merged.length - 1];

    if (current.startSecond <= previous.endSecond + 1) {
      previous.endSecond = Math.max(previous.endSecond, current.endSecond);
      continue;
    }

    const gapStart = previous.endSecond + 1;
    const gapEnd = current.startSecond - 1;
    const gapSeconds = gapEnd - gapStart + 1;

    if (gapSeconds <= 0 || gapSeconds > configuration.maxMergeGapSeconds) {
      merged.push({ ...current });
      continue;
    }

    const gapPowerValues = smoothThirtySecondPower.slice(gapStart, gapEnd + 1).filter((value): value is number => value !== null);
    const gapAveragePower = gapPowerValues.length > 0
      ? gapPowerValues.reduce((sum, value) => sum + value, 0) / gapPowerValues.length
      : 0;

    if (gapSeconds <= configuration.maxShortRecoveryMergeSeconds && gapAveragePower / ftp >= configuration.shortRecoveryMergeMinPowerRatio) {
      previous.endSecond = current.endSecond;
      continue;
    }

    if (gapAveragePower / ftp >= 0.35) {
      previous.endSecond = current.endSecond;
      continue;
    }

    merged.push({ ...current });
  }

  return merged;
}

function getIntervalSlice(timeline: TimelineSecond[], block: IntervalBlock): TimelineSecond[] {
  return timeline.slice(block.startSecond, block.endSecond + 1);
}

function fitWithinRange(value: number, minValue: number, maxValue: number): number {
  if (value >= minValue && value <= maxValue) {
    return 1;
  }

  const distance = value < minValue ? (minValue - value) / Math.max(1, minValue) : (value - maxValue) / Math.max(1, maxValue);
  return clamp(1 - distance, 0, 1);
}

function classifyInterval(durationSeconds: number, averagePowerRatio: number): { classification: IntervalClassification; fitScore: number } {
  let bestClassification: IntervalClassification = "unclassified";
  let bestScore = 0;

  for (const profile of CLASSIFICATION_PROFILES) {
    const durationFit = fitWithinRange(durationSeconds, profile.minDurationSeconds, profile.maxDurationSeconds);
    const intensityFit = profile.maxRatio === Number.POSITIVE_INFINITY
      ? averagePowerRatio >= profile.minRatio
        ? 1
        : clamp(averagePowerRatio / profile.minRatio, 0, 1)
      : fitWithinRange(averagePowerRatio, profile.minRatio, profile.maxRatio);

    const score = 0.45 * durationFit + 0.55 * intensityFit;
    if (score > bestScore) {
      bestScore = score;
      bestClassification = profile.classification;
    }
  }

  if (bestScore < 0.5) {
    return { classification: "unclassified", fitScore: bestScore };
  }

  return { classification: bestClassification, fitScore: bestScore };
}

function minimumDurationForClassification(
  classification: IntervalClassification,
  configuration: IntervalDetectionConfiguration
): number {
  switch (classification) {
    case "sprint":
      return configuration.minSprintDurationSeconds;
    case "anaerobic":
      return configuration.minAnaerobicDurationSeconds;
    case "vo2max":
      return configuration.minVo2maxDurationSeconds;
    case "threshold":
      return configuration.minThresholdDurationSeconds;
    case "sweet-spot":
      return configuration.minSweetSpotDurationSeconds;
    case "tempo":
      return configuration.minTempoDurationSeconds;
    case "endurance":
      return configuration.minEnduranceDurationSeconds;
    case "unclassified":
    default:
      return configuration.minAnaerobicDurationSeconds;
  }
}

function averageRatioInWindow(powerBySecond: Array<number | null>, ftp: number, start: number, end: number): number | null {
  const values = powerBySecond.slice(start, end + 1).filter((value): value is number => value !== null);
  if (values.length === 0) {
    return null;
  }

  const averagePower = values.reduce((sum, value) => sum + value, 0) / values.length;
  return averagePower / ftp;
}

function buildInterval(
  block: IntervalBlock,
  intervalNumber: number,
  timeline: TimelineSecond[],
  ftp: number,
  classificationFit: number
): DetectedWorkInterval {
  const slice = getIntervalSlice(timeline, block);
  const durationSeconds = Math.max(0, block.endSecond - block.startSecond + 1);
  const powerBySecond = slice.map(item => (item.stopped ? null : item.powerWatts));
  const heartRateBySecond = slice.map(item => (item.stopped ? null : item.heartRateBpm));
  const cadenceBySecond = slice.map(item => (item.stopped ? null : item.cadenceRpm));

  const averagePower = average(powerBySecond.map(value => (value === null ? 0 : value)));
  const normalizedPower = durationSeconds >= 30 ? computeNormalizedPower(powerBySecond) : null;
  const maxPower = max(powerBySecond);
  const averageHeartRate = average(heartRateBySecond);
  const maxHeartRate = max(heartRateBySecond);
  const averageCadence = average(cadenceBySecond);

  const workKilojoules = averagePower !== null ? (averagePower * durationSeconds) / 1000 : null;
  const percentOfFTP = averagePower !== null ? (averagePower / ftp) * 100 : null;
  const intensityFactor = averagePower !== null
    ? ((normalizedPower ?? averagePower) / ftp)
    : null;

  const averagePowerRatio = averagePower !== null ? averagePower / ftp : 0;
  const { classification } = classifyInterval(durationSeconds, averagePowerRatio);

  const powerCoverage = powerBySecond.filter(value => value !== null).length / Math.max(1, durationSeconds);
  const continuity = slice.filter(item => !item.stopped).length / Math.max(1, durationSeconds);

  const preRatio = averageRatioInWindow(
    timeline.map(item => (item.stopped ? null : item.powerWatts)),
    ftp,
    Math.max(0, block.startSecond - 60),
    Math.max(0, block.startSecond - 1)
  );
  const postRatio = averageRatioInWindow(
    timeline.map(item => (item.stopped ? null : item.powerWatts)),
    ftp,
    Math.min(timeline.length - 1, block.endSecond + 1),
    Math.min(timeline.length - 1, block.endSecond + 60)
  );

  let separationScore = 0.4;
  if (preRatio !== null && postRatio !== null) {
    if (preRatio < averagePowerRatio * 0.75 && postRatio < averagePowerRatio * 0.75) {
      separationScore = 1;
    } else if (preRatio < averagePowerRatio * 0.85 || postRatio < averagePowerRatio * 0.85) {
      separationScore = 0.7;
    }
  }

  const confidence = clamp(
    (classificationFit * 0.4) + (continuity * 0.2) + (powerCoverage * 0.2) + (separationScore * 0.2),
    0,
    1
  );

  return {
    id: `interval-${intervalNumber}`,
    startTimestamp: timeline[block.startSecond]?.timestamp ?? null,
    endTimestamp: timeline[block.endSecond]?.timestamp ?? null,
    startElapsedSeconds: timeline[block.startSecond]?.elapsedSeconds ?? block.startSecond,
    endElapsedSeconds: timeline[block.endSecond]?.elapsedSeconds ?? block.endSecond,
    durationSeconds,
    averagePower,
    normalizedPower,
    maxPower,
    averageHeartRate,
    maxHeartRate,
    averageCadence,
    workKilojoules,
    intensityFactor,
    percentOfFTP,
    classification,
    confidence,
    source: "power-smoothed",
    setId: null,
    repetitionNumber: null,
    totalRepetitionsInSet: null
  };
}

function buildRecoveries(
  intervals: DetectedWorkInterval[],
  timeline: TimelineSecond[],
  ftp: number,
  configuration: IntervalDetectionConfiguration
): DetectedRecoveryInterval[] {
  const recoveries: DetectedRecoveryInterval[] = [];

  for (let index = 0; index < intervals.length; index += 1) {
    const current = intervals[index];
    const next = intervals[index + 1];

    const recoveryStartSecond = current.endElapsedSeconds + 1;
    const recoveryEndSecond = next ? next.startElapsedSeconds - 1 : timeline.length - 1;

    if (recoveryEndSecond < recoveryStartSecond) {
      continue;
    }

    const slice = timeline.slice(recoveryStartSecond, recoveryEndSecond + 1).filter(item => !item.stopped);
    const durationSeconds = slice.length;

    if (durationSeconds < configuration.minRecoveryDurationSeconds) {
      continue;
    }

    const averagePower = average(slice.map(item => (item.powerWatts === null ? 0 : item.powerWatts)));
    const averageHeartRate = average(slice.map(item => item.heartRateBpm));
    const powerPercentOfFTP = averagePower !== null ? (averagePower / ftp) * 100 : null;

    recoveries.push({
      startTimestamp: timeline[recoveryStartSecond]?.timestamp ?? null,
      endTimestamp: timeline[recoveryEndSecond]?.timestamp ?? null,
      durationSeconds,
      averagePower,
      averageHeartRate,
      powerPercentOfFTP,
      associatedIntervalId: current.id
    });
  }

  return recoveries;
}

function sameSetCandidate(
  previous: DetectedWorkInterval,
  current: DetectedWorkInterval,
  recovery: DetectedRecoveryInterval | undefined
): boolean {
  if (previous.classification === "unclassified" || current.classification === "unclassified") {
    return false;
  }

  if (previous.classification !== current.classification) {
    return false;
  }

  const durationTolerance = Math.max(20, previous.durationSeconds * 0.25);
  if (Math.abs(previous.durationSeconds - current.durationSeconds) > durationTolerance) {
    return false;
  }

  if (previous.percentOfFTP === null || current.percentOfFTP === null) {
    return false;
  }

  if (Math.abs(previous.percentOfFTP - current.percentOfFTP) > 12) {
    return false;
  }

  if (!recovery) {
    return false;
  }

  if (recovery.durationSeconds < 30 || recovery.durationSeconds > 900) {
    return false;
  }

  if (recovery.powerPercentOfFTP !== null && recovery.powerPercentOfFTP > 75) {
    return false;
  }

  return true;
}

function assignSets(intervals: DetectedWorkInterval[], recoveries: DetectedRecoveryInterval[]): DetectedWorkInterval[] {
  if (intervals.length === 0) {
    return intervals;
  }

  const recoveryByIntervalId = new Map<string, DetectedRecoveryInterval>();
  for (const recovery of recoveries) {
    recoveryByIntervalId.set(recovery.associatedIntervalId, recovery);
  }

  const groups: number[][] = [];
  let currentGroup: number[] = [0];

  for (let index = 1; index < intervals.length; index += 1) {
    const previous = intervals[index - 1];
    const current = intervals[index];
    const previousRecovery = recoveryByIntervalId.get(previous.id);

    if (sameSetCandidate(previous, current, previousRecovery)) {
      currentGroup.push(index);
      continue;
    }

    groups.push(currentGroup);
    currentGroup = [index];
  }

  groups.push(currentGroup);

  let setCounter = 1;
  const updated = intervals.map(interval => ({ ...interval }));

  for (const group of groups) {
    if (group.length < 2) {
      continue;
    }

    const setId = `set-${setCounter}`;
    setCounter += 1;

    for (let repetitionIndex = 0; repetitionIndex < group.length; repetitionIndex += 1) {
      const intervalIndex = group[repetitionIndex];
      updated[intervalIndex].setId = setId;
      updated[intervalIndex].repetitionNumber = repetitionIndex + 1;
      updated[intervalIndex].totalRepetitionsInSet = group.length;
    }
  }

  return updated;
}

function summarize(
  status: IntervalDetectionResult["status"],
  intervals: DetectedWorkInterval[],
  continuousEffortSegments: DetectedWorkInterval[],
  recoveries: DetectedRecoveryInterval[],
  configuration: IntervalDetectionConfiguration,
  warnings: string[]
): IntervalDetectionResult {
  return {
    status,
    detectedIntervals: intervals,
    continuousEffortSegments,
    detectedRecoveries: recoveries,
    intervalCount: intervals.length,
    continuousSegmentCount: continuousEffortSegments.length,
    totalWorkDurationSeconds: intervals.reduce((sum, interval) => sum + interval.durationSeconds, 0),
    totalContinuousDurationSeconds: continuousEffortSegments.reduce((sum, interval) => sum + interval.durationSeconds, 0),
    totalRecoveryDurationSeconds: recoveries.reduce((sum, recovery) => sum + recovery.durationSeconds, 0),
    configurationUsed: configuration,
    warnings
  };
}

export function detectIntervals(
  records: RideRecord[],
  ftpInput: number | null | undefined,
  configurationInput?: IntervalDetectionInputConfiguration
): IntervalDetectionResult {
  const configuration = mergeConfiguration(configurationInput);
  const warnings: string[] = [];

  if (records.length < 2) {
    return summarize("insufficient-data", [], [], [], configuration, ["Not enough ride records to detect intervals."]);
  }

  const ftp = normalizeFtp(ftpInput);
  if (ftp === null) {
    return summarize("invalid-ftp", [], [], [], configuration, ["FTP is invalid; interval detection requires a positive FTP value."]);
  }

  const { timeline, medianIntervalSeconds } = buildTimeline(records, configuration);
  if (timeline.length < 2 || medianIntervalSeconds === null) {
    return summarize("insufficient-data", [], [], [], configuration, ["Unable to build a usable timeline for interval detection."]);
  }

  const smooth5 = movingAveragePowerBySecond(timeline, 5);
  const smooth10 = movingAveragePowerBySecond(timeline, 10);
  const smooth30 = movingAveragePowerBySecond(timeline, 30);
  const smooth45 = movingAveragePowerBySecond(timeline, 45);
  const smooth60 = movingAveragePowerBySecond(timeline, 60);
  const smooth90 = movingAveragePowerBySecond(timeline, 90);

  const blockDetectionOptions: BlockDetectionOptions = {
    startHysteresisSeconds: configuration.startHysteresisSeconds,
    endHysteresisSeconds: configuration.endHysteresisSeconds,
    maxPowerDipSeconds: configuration.maxPowerDipSeconds,
    maxCadenceInterruptionSeconds: configuration.maxCadenceInterruptionSeconds,
    rampLeadInSeconds: configuration.rampLeadInSeconds,
    startThresholdRatioOffset: configuration.startThresholdRatioOffset,
    endThresholdRatioOffset: configuration.endThresholdRatioOffset
  };

  const candidateBlocks = [
    ...detectBlocks(timeline, smooth5, ftp, 1.5, configuration.minSprintDurationSeconds, 2, blockDetectionOptions),
    ...detectBlocks(timeline, smooth10, ftp, 1.15, configuration.minAnaerobicDurationSeconds, 4, blockDetectionOptions),
    ...detectBlocks(timeline, smooth30, ftp, 1.0, configuration.minVo2maxDurationSeconds, 8, blockDetectionOptions),
    ...detectBlocks(timeline, smooth45, ftp, 0.88, configuration.minThresholdDurationSeconds, 8, blockDetectionOptions),
    ...detectBlocks(timeline, smooth60, ftp, 0.76, configuration.minTempoDurationSeconds, 10, blockDetectionOptions),
    ...detectBlocks(timeline, smooth90, ftp, 0.56, configuration.minEnduranceDurationSeconds, 12, blockDetectionOptions)
  ];

  if (candidateBlocks.length === 0) {
    return summarize("no-intervals", [], [], [], configuration, ["No meaningful intervals detected."]);
  }

  const mergedBlocks = mergeBlocks(candidateBlocks, configuration, smooth30, ftp);

  const provisionalIntervals = mergedBlocks
    .map((block, index) => {
      const powerSlice = getIntervalSlice(timeline, block).map(item => (item.powerWatts === null ? 0 : item.powerWatts));
      const averagePower = powerSlice.length > 0
        ? powerSlice.reduce((sum, value) => sum + value, 0) / powerSlice.length
        : 0;
      const ratio = averagePower / ftp;
      const { fitScore } = classifyInterval(block.endSecond - block.startSecond + 1, ratio);

      return buildInterval(block, index + 1, timeline, ftp, fitScore);
    })
    .filter(interval => interval.durationSeconds >= minimumDurationForClassification(interval.classification, configuration))
    .filter(interval => interval.confidence >= configuration.minConfidence)
    .sort((left, right) => left.startElapsedSeconds - right.startElapsedSeconds)
    .map((interval, index) => ({
      ...interval,
      id: `interval-${index + 1}`
    }));

  if (provisionalIntervals.length === 0) {
    return summarize("no-intervals", [], [], [], configuration, ["No meaningful intervals detected."]);
  }

  const recoveries = buildRecoveries(provisionalIntervals, timeline, ftp, configuration);
  const intervalsWithSets = assignSets(provisionalIntervals, recoveries);
  const structuredIntervals = intervalsWithSets.filter(interval => interval.setId !== null);
  const structuredIntervalIds = new Set(structuredIntervals.map(interval => interval.id));
  const continuousEffortSegments = intervalsWithSets.filter(interval => interval.setId === null);
  const structuredRecoveries = recoveries.filter(recovery => structuredIntervalIds.has(recovery.associatedIntervalId));

  if (
    structuredIntervals.length === 0 &&
    continuousEffortSegments.length === 1 &&
    continuousEffortSegments[0].classification === "endurance" &&
    continuousEffortSegments[0].durationSeconds / timeline.length > configuration.maxSingleEnduranceCoverage
  ) {
    warnings.push("Detected riding appears to be a continuous endurance effort, not a repeated interval set.");
  }

  if (structuredIntervals.length > 20) {
    warnings.push("High interval count detected; review may include fragmented efforts.");
  }

  return summarize(
    structuredIntervals.length > 0 || continuousEffortSegments.length > 0 ? "available" : "no-intervals",
    structuredIntervals,
    continuousEffortSegments,
    structuredRecoveries,
    configuration,
    warnings
  );
}
